/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  mergeSortByParameter,
  optimisticLockingOf,
  RestSettings,
  SlicedDataQuerySorting,
  SlicedDataSource,
} from '@gh/core-data';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { CheckUsageDto } from '../common/check-usage.dto';
import { CheckUsageMapper } from '../common/check-usage.mapper';
import { ComponentGridFilter } from '../components';
import { ItemBulkUpdateListItemDto } from './item-bulk-update-list-item.dto';
import { ItemBulkUpdateListItemMapper } from './item-bulk-update-list-item.mapper';
import { ItemDetailsDto } from './item-details.dto';
import { ItemDetailsMapper } from './item-details.mapper';
import { ItemsListItemDto } from './items-list-item.dto';
import { ItemsListItemMapper } from './items-list-item.mapper';

export type ItemsGridFilter = {
  showDeleted: boolean;
  searchString: string;
};

@Injectable()
export class ItemRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  create(item: ItemDetailsDto): Observable<ItemDetailsDto> {
    return this.httpPost(
      `${this.bp}/items`,
      item,
      optimisticLockingOf(ItemDetailsMapper));
  }

  update(item: ItemDetailsDto): Observable<ItemDetailsDto> {
    return this.httpPut(`${this.bp}/items/${item.id}`, item, optimisticLockingOf(ItemDetailsMapper));
  }

  get(id: ID): Observable<ItemDetailsDto> {
    return this.httpGet(`${this.bp}/items/${id}`, optimisticLockingOf(ItemDetailsMapper));
  }

  findAllAsDataSource(filter: ItemsGridFilter): SlicedDataSource<ItemsListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/items`,
      { autocomplete: false, ...filter },
      void 0,
      arrayOf(ItemsListItemMapper));
  }

  getIndex(id: ID, filter: ComponentGridFilter, sorting?: SlicedDataQuerySorting): Observable<number> {
    return this.httpGet(
      mergeSortByParameter(`${this.bp}/items/${id}/index`, sorting),
      { autocomplete: false, ...filter },
      void 0,
      identity()).pipe(
      map$(({ index }) => index));
  }

  undelete(item: ItemsListItemDto): Observable<ItemsListItemDto> {
    return this.httpPut(
      `${this.bp}/items/undelete/${item.id}`, void 0, optimisticLockingOf(ItemsListItemMapper));
  }

  checkUsage(item: ItemsListItemDto): Observable<CheckUsageDto> {
    const { id, version } = item;
    return this.httpGet(
      `${this.bp}/items/check-usage/${id}`,
      { version },
      void 0,
      CheckUsageMapper,
    );
  }

  deactivateOrDelete(item: ItemsListItemDto, checkUsage: CheckUsageDto): Observable<CheckUsageDto> {
    return this.httpDelete(
      `${this.bp}/items/${item.id}`, checkUsage, CheckUsageMapper);
  }

  getItemListByComponent(id: ID): Observable<ItemBulkUpdateListItemDto[]> {
    return this.httpGet(`${this.bp}/bulk-update/item/list/${id}`, arrayOf(ItemBulkUpdateListItemMapper));
  }

  bulkDelete(items: any[]): Observable<any> {
    return this.httpDelete(`${this.bp}/items`, items, identity());
  }

  getItemCodes(searchString: string): Observable<string[]> {
    return this.httpGet(
      `${this.bp}/items/codes`,
      { searchString },
      void 0,
      identity()
    ).pipe(map$(response => {
      return (response as any).codes as string[];
    }));
  }
}
